import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Icons } from '../components/Icons';
import Barcode from 'react-barcode';
import QRCode from 'qrcode';

function BarcodePrint() {
  const [searchParams] = useSearchParams();
  const preselectId = searchParams.get('preselect');

  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [barcodeSize, setBarcodeSize] = useState('medium'); // small, medium, large
  const [codeType, setCodeType] = useState('barcode'); // 'barcode' or 'qrcode'
  const [showSettings, setShowSettings] = useState(false);
  const [qrCodes, setQrCodes] = useState({}); // QR 코드 이미지 저장

  // 용지 타입 선택
  const [paperType, setPaperType] = useState('label'); // 'label', 'thermal', 'custom'
  const [selectedLabelPaper, setSelectedLabelPaper] = useState('formtec3100');

  // 라벨 용지 프리셋 데이터
  const labelPaperPresets = {
    formtec3100: { name: 'Formtec 3100 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 38.1, labelHeight: 21.2, columns: 5, rows: 13, marginTop: 10.7, marginLeft: 4.6, gapHorizontal: 2.5, gapVertical: 0 },
    formtec3101: { name: 'Formtec 3101 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 63.5, labelHeight: 38.1, columns: 3, rows: 7, marginTop: 15.1, marginLeft: 8.5, gapHorizontal: 2.5, gapVertical: 0 },
    formtec3102: { name: 'Formtec 3102 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 63.5, labelHeight: 46.6, columns: 3, rows: 6, marginTop: 8.3, marginLeft: 8.5, gapHorizontal: 2.5, gapVertical: 0 },
    formtec3104: { name: 'Formtec 3104 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 99.1, labelHeight: 38.1, columns: 2, rows: 7, marginTop: 15.1, marginLeft: 4.7, gapHorizontal: 2.5, gapVertical: 0 },
    formtec3105: { name: 'Formtec 3105 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 99.1, labelHeight: 57.0, columns: 2, rows: 5, marginTop: 7.0, marginLeft: 4.7, gapHorizontal: 2.5, gapVertical: 0 },
    formtec3107: { name: 'Formtec 3107 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 63.5, labelHeight: 72.0, columns: 3, rows: 4, marginTop: 4.5, marginLeft: 8.5, gapHorizontal: 2.5, gapVertical: 0 },
    printecV3510: { name: 'Printec V3510 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 38.1, labelHeight: 21.2, columns: 5, rows: 13, marginTop: 10.7, marginLeft: 4.6, gapHorizontal: 2.5, gapVertical: 0 },
    printecV3520: { name: 'Printec V3520 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 63.5, labelHeight: 38.1, columns: 3, rows: 7, marginTop: 15.1, marginLeft: 8.5, gapHorizontal: 2.5, gapVertical: 0 },
    printecV3530: { name: 'Printec V3530 (A4)', paperWidth: 210, paperHeight: 297, labelWidth: 63.5, labelHeight: 46.6, columns: 3, rows: 6, marginTop: 8.3, marginLeft: 8.5, gapHorizontal: 2.5, gapVertical: 0 },
  };

  // 감열지 설정
  const [thermalSettings, setThermalSettings] = useState({
    width: 60,
    height: 35,
  });

  // 사용자 정의 설정 (mm 단위)
  const [customSettings, setCustomSettings] = useState({
    paperWidth: 210,
    paperHeight: 297,
    labelWidth: 38.1,
    labelHeight: 21.2,
    columns: 5,
    rows: 13,
    marginTop: 10.7,
    marginLeft: 4.6,
    gapHorizontal: 2.5,
    gapVertical: 0,
  });

  // 현재 적용된 설정 계산
  const getCurrentPaperSettings = () => {
    if (paperType === 'label') {
      return labelPaperPresets[selectedLabelPaper];
    } else if (paperType === 'thermal') {
      return {
        paperWidth: thermalSettings.width,
        paperHeight: thermalSettings.height,
        labelWidth: thermalSettings.width,
        labelHeight: thermalSettings.height,
        columns: 1,
        rows: 1,
        marginTop: 0,
        marginLeft: 0,
        gapHorizontal: 0,
        gapVertical: 0,
      };
    } else {
      return customSettings;
    }
  };

  // 프린터 설정
  const [printerSettings, setPrinterSettings] = useState({
    barcodeWidth: 80,       // 바코드 너비 (px)
    barcodeHeight: 50,      // 바코드 높이 (px)
    columns: 3,             // 가로 칸수
    rows: 8,                // 세로 줄수 (0 = 자동)
    startPosition: 1,       // 시작 위치 (1부터 시작)
    marginTop: 10,          // 상단 여백 (mm)
    marginBottom: 10,       // 하단 여백 (mm)
    marginLeft: 10,         // 좌측 여백 (mm)
    marginRight: 10,        // 우측 여백 (mm)
    gapHorizontal: 8,       // 가로 간격 (px)
    gapVertical: 8,         // 세로 간격 (px)
  });

  // 프리셋 관리
  const [savedPresets, setSavedPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [showPresetSave, setShowPresetSave] = useState(false);

  // 표시 옵션
  const [displayOptions, setDisplayOptions] = useState({
    showName: true,      // 제품명
    showPrice: false,    // 가격
    showBarcode: false,  // 바코드 번호
    showLocation: false, // 위치
    showCategory: false, // 카테고리
  });

  // 선택된 재고 계산
  const selectedInventory = useMemo(() => {
    return inventory.filter(item => selectedItems.includes(item.id));
  }, [inventory, selectedItems]);

  useEffect(() => {
    loadInventory();
    loadPresets();
  }, []);

  // URL 파라미터로 제품 미리 선택
  useEffect(() => {
    if (preselectId && inventory.length > 0 && !selectedItems.includes(preselectId)) {
      // 해당 제품이 존재하는지 확인
      const itemExists = inventory.some(item => item.id === preselectId);
      if (itemExists) {
        setSelectedItems([preselectId]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectId, inventory]);

  // QR 코드 생성
  useEffect(() => {
    if (codeType === 'qrcode' && selectedInventory.length > 0) {
      const generateQRCodes = async () => {
        const codes = {};
        for (const item of selectedInventory) {
          if (item.barcodeId) {
            try {
              const qrDataUrl = await QRCode.toDataURL(item.barcodeId, {
                width: 200,
                margin: 1,
              });
              codes[item.id] = qrDataUrl;
            } catch (error) {
              console.error('QR 코드 생성 오류:', error);
            }
          }
        }
        setQrCodes(codes);
      };
      generateQRCodes();
    }
  }, [codeType, selectedInventory]);

  // 프리셋 불러오기
  const loadPresets = () => {
    try {
      const saved = localStorage.getItem('barcodePrinterPresets');
      if (saved) {
        setSavedPresets(JSON.parse(saved));
      }
    } catch (error) {
      console.error('프리셋 로드 오류:', error);
    }
  };

  // 프리셋 저장
  const savePreset = () => {
    if (!presetName.trim()) {
      alert('프리셋 이름을 입력하세요');
      return;
    }

    const newPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      settings: { ...printerSettings }
    };

    const updatedPresets = [...savedPresets, newPreset];
    setSavedPresets(updatedPresets);
    localStorage.setItem('barcodePrinterPresets', JSON.stringify(updatedPresets));

    setPresetName('');
    setShowPresetSave(false);
    alert(`프리셋 "${newPreset.name}"이(가) 저장되었습니다`);
  };

  // 프리셋 적용
  const loadPreset = (preset) => {
    setPrinterSettings(preset.settings);
    alert(`프리셋 "${preset.name}"을(를) 적용했습니다`);
  };

  // 프리셋 삭제
  const deletePreset = (presetId) => {
    if (window.confirm('이 프리셋을 삭제하시겠습니까?')) {
      const updatedPresets = savedPresets.filter(p => p.id !== presetId);
      setSavedPresets(updatedPresets);
      localStorage.setItem('barcodePrinterPresets', JSON.stringify(updatedPresets));
      alert('프리셋이 삭제되었습니다');
    }
  };

  const loadInventory = async () => {
    try {
      const inventoryRef = collection(db, 'inventory');
      const q = query(inventoryRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setInventory(items);
    } catch (error) {
      console.error('재고 로드 오류:', error);
      alert('재고를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.barcodeId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleAll = () => {
    if (selectedItems.length === filteredInventory.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredInventory.map(item => item.id));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getSizeConfig = () => {
    // 바코드 라이브러리의 width는 배율값이므로 px를 배율로 변환
    const widthScale = printerSettings.barcodeWidth / 60; // 60px = 1배율 기준
    return {
      width: Math.max(0.5, Math.min(3, widthScale)), // 배율 0.5~3 범위 제한
      height: printerSettings.barcodeHeight,
      fontSize: Math.max(8, printerSettings.barcodeHeight / 4), // 최소 8px
    };
  };

  const sizeConfig = getSizeConfig();

  // 현재 용지 설정 가져오기
  const currentPaperSettings = getCurrentPaperSettings();

  // 시작 위치를 고려한 빈 칸 계산
  const emptySlots = printerSettings.startPosition - 1;
  const printItems = [
    ...Array(emptySlots).fill(null),
    ...selectedInventory
  ];

  // 인쇄할 전체 그리드 칸 수 계산 (미리보기와 동일하게)
  const totalPrintSlots = currentPaperSettings.rows > 0
    ? currentPaperSettings.columns * currentPaperSettings.rows
    : Math.max(currentPaperSettings.columns * 8, printItems.length);

  // 전체 그리드 배열 생성 (실제 인쇄용)
  const fullGridItems = Array.from({ length: totalPrintSlots }, (_, index) => {
    return printItems[index] || null;
  });

  const handleSettingChange = (key, value) => {
    setPrinterSettings(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-brand-900">
          {codeType === 'barcode' ? '바코드' : 'QR코드'} 출력
        </h2>
        <p className="text-sm text-gray-500">
          제품별 {codeType === 'barcode' ? '바코드' : 'QR코드'}를 선택하여 출력합니다.
        </p>
      </div>

      {/* 인쇄용 스타일 */}
      <style>
        {`
          @page {
            size: ${currentPaperSettings.paperWidth}mm ${currentPaperSettings.paperHeight}mm;
            margin: ${currentPaperSettings.marginTop}mm 0 0 ${currentPaperSettings.marginLeft}mm;
          }
          @media print {
            body * {
              visibility: hidden;
            }
            #print-area, #print-area * {
              visibility: visible;
            }
            #print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: ${currentPaperSettings.paperWidth}mm;
            }
            .barcode-item {
              page-break-inside: avoid;
              break-inside: avoid;
              width: ${currentPaperSettings.labelWidth}mm;
              height: ${currentPaperSettings.labelHeight}mm;
              margin-right: ${currentPaperSettings.gapHorizontal}mm;
              margin-bottom: ${currentPaperSettings.gapVertical}mm;
            }
          }
        `}
      </style>

      {/* 선택 및 설정 영역 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* 검색 */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-brand-700 mb-2">제품 검색</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="제품명 또는 바코드로 검색..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          {/* 코드 타입 선택 */}
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-2">코드 타입</label>
            <select
              value={codeType}
              onChange={(e) => setCodeType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
            >
              <option value="barcode">바코드</option>
              <option value="qrcode">QR 코드</option>
            </select>
          </div>

          {/* 바코드 크기 */}
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-2">크기</label>
            <select
              value={barcodeSize}
              onChange={(e) => setBarcodeSize(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
            >
              <option value="small">작게</option>
              <option value="medium">보통</option>
              <option value="large">크게</option>
            </select>
          </div>
        </div>

        {/* 선택 정보 및 버튼 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleAll}
              className="px-4 py-2 border border-brand-600 text-brand-600 rounded-lg hover:bg-brand-50 transition font-medium"
            >
              {selectedItems.length === filteredInventory.length ? '전체 해제' : '전체 선택'}
            </button>
            <span className="text-sm text-gray-600">
              {selectedItems.length}개 선택됨
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium flex items-center"
            >
              <Icons.Settings className="w-5 h-5 mr-2" />
              프린터 설정
            </button>
            <button
              onClick={handlePrint}
              disabled={selectedItems.length === 0}
              className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Icons.Printer className="w-5 h-5 mr-2" />
              {codeType === 'barcode' ? '바코드' : 'QR코드'} 출력
            </button>
          </div>
        </div>
      </div>

      {/* 프린터 설정 패널 */}
      {showSettings && (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 mb-6 print:hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-brand-900">용지 설정</h3>
            <button
              onClick={() => setShowSettings(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <Icons.X className="w-5 h-5" />
            </button>
          </div>

          {/* 용지 타입 선택 */}
          <div className="flex items-center gap-8 mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="paperType"
                checked={paperType === 'label'}
                onChange={() => setPaperType('label')}
                className="w-5 h-5 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-gray-700">라벨 용지</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="paperType"
                checked={paperType === 'thermal'}
                onChange={() => setPaperType('thermal')}
                className="w-5 h-5 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-gray-700">감열지</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="paperType"
                checked={paperType === 'custom'}
                onChange={() => setPaperType('custom')}
                className="w-5 h-5 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-gray-700">사용자 정의</span>
            </label>
          </div>

          {/* 라벨 용지 선택 */}
          {paperType === 'label' && (
            <div className="space-y-4">
              <select
                value={selectedLabelPaper}
                onChange={(e) => setSelectedLabelPaper(e.target.value)}
                className="w-full max-w-md px-4 py-3 border border-brand-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white text-gray-900"
              >
                <optgroup label="Formtec">
                  <option value="formtec3100">Formtec 3100 (A4) - 5×13칸</option>
                  <option value="formtec3101">Formtec 3101 (A4) - 3×7칸</option>
                  <option value="formtec3102">Formtec 3102 (A4) - 3×6칸</option>
                  <option value="formtec3104">Formtec 3104 (A4) - 2×7칸</option>
                  <option value="formtec3105">Formtec 3105 (A4) - 2×5칸</option>
                  <option value="formtec3107">Formtec 3107 (A4) - 3×4칸</option>
                </optgroup>
                <optgroup label="Printec">
                  <option value="printecV3510">Printec V3510 (A4) - 5×13칸</option>
                  <option value="printecV3520">Printec V3520 (A4) - 3×7칸</option>
                  <option value="printecV3530">Printec V3530 (A4) - 3×6칸</option>
                </optgroup>
              </select>

              {/* 선택된 라벨 정보 표시 */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {labelPaperPresets[selectedLabelPaper].name}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600">
                  <div>
                    <span className="text-gray-500">용지:</span>{' '}
                    {labelPaperPresets[selectedLabelPaper].paperWidth} × {labelPaperPresets[selectedLabelPaper].paperHeight}mm
                  </div>
                  <div>
                    <span className="text-gray-500">라벨:</span>{' '}
                    {labelPaperPresets[selectedLabelPaper].labelWidth} × {labelPaperPresets[selectedLabelPaper].labelHeight}mm
                  </div>
                  <div>
                    <span className="text-gray-500">배열:</span>{' '}
                    {labelPaperPresets[selectedLabelPaper].columns} × {labelPaperPresets[selectedLabelPaper].rows}칸
                  </div>
                  <div>
                    <span className="text-gray-500">총:</span>{' '}
                    {labelPaperPresets[selectedLabelPaper].columns * labelPaperPresets[selectedLabelPaper].rows}개
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 감열지 설정 */}
          {paperType === 'thermal' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">너비</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={thermalSettings.width}
                      onChange={(e) => setThermalSettings({...thermalSettings, width: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-sm text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">높이</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={thermalSettings.height}
                      onChange={(e) => setThermalSettings({...thermalSettings, height: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-sm text-gray-500">mm</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">감열지 프린터 (영수증 프린터)용 설정입니다.</p>
            </div>
          )}

          {/* 사용자 정의 설정 */}
          {paperType === 'custom' && (
            <div className="space-y-6">
              {/* 시각적 다이어그램 */}
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="relative mx-auto bg-white border-2 border-gray-300 rounded" style={{ width: '300px', height: '200px' }}>
                  {/* 용지 상단 여백 */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 text-xs text-red-500 flex flex-col items-center" style={{ top: '2px' }}>
                    <span>↕ 용지 상단 여백</span>
                  </div>

                  {/* 용지 왼쪽 여백 */}
                  <div className="absolute left-1 top-1/2 transform -translate-y-1/2 text-xs text-red-500 writing-mode-vertical" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg) translateX(50%)' }}>
                    ↔ 용지 왼쪽 여백
                  </div>

                  {/* 라벨 그리드 */}
                  <div className="absolute inset-0 flex items-center justify-center p-8">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="w-16 h-12 border-2 border-dashed border-brand-400 bg-brand-50 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">라벨 높이</div>
                          <div className="text-xs text-gray-500">↕</div>
                        </div>
                      </div>
                      <div className="w-16 h-12 border-2 border-dashed border-brand-400 bg-brand-50 flex items-center justify-center relative">
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 text-xs text-red-500">
                          ↔ 라벨 오른쪽 여백
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">라벨 너비</div>
                          <div className="text-xs text-gray-500">↔</div>
                        </div>
                      </div>
                      <div className="w-16 h-12 border-2 border-dashed border-gray-300 bg-gray-50"></div>
                      <div className="w-16 h-12 border-2 border-dashed border-gray-300 bg-gray-50"></div>
                    </div>
                  </div>

                  {/* 라벨 하단 여백 */}
                  <div className="absolute bottom-1 left-8 text-xs text-red-500">
                    ↕ 라벨 하단 여백
                  </div>
                </div>
              </div>

              {/* 설정 입력 필드 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">용지 너비</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.paperWidth}
                      onChange={(e) => setCustomSettings({...customSettings, paperWidth: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">용지 높이</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.paperHeight}
                      onChange={(e) => setCustomSettings({...customSettings, paperHeight: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">라벨 너비</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.labelWidth}
                      onChange={(e) => setCustomSettings({...customSettings, labelWidth: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">라벨 높이</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.labelHeight}
                      onChange={(e) => setCustomSettings({...customSettings, labelHeight: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">라벨 가로 개수</label>
                  <input
                    type="number"
                    min="1"
                    value={customSettings.columns}
                    onChange={(e) => setCustomSettings({...customSettings, columns: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">라벨 세로 개수</label>
                  <input
                    type="number"
                    min="1"
                    value={customSettings.rows}
                    onChange={(e) => setCustomSettings({...customSettings, rows: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">용지 상단 여백</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.marginTop}
                      onChange={(e) => setCustomSettings({...customSettings, marginTop: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">용지 왼쪽 여백</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.marginLeft}
                      onChange={(e) => setCustomSettings({...customSettings, marginLeft: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">라벨 오른쪽 여백</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.gapHorizontal}
                      onChange={(e) => setCustomSettings({...customSettings, gapHorizontal: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">라벨 하단 여백</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={customSettings.gapVertical}
                      onChange={(e) => setCustomSettings({...customSettings, gapVertical: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <span className="ml-2 text-xs text-gray-500">mm</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 구분선 */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* 추가 설정 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 시작 위치 */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 text-sm">시작 위치</h4>
              <div>
                <label className="block text-xs text-gray-600 mb-1">시작할 칸 번호</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={printerSettings.startPosition}
                  onChange={(e) => handleSettingChange('startPosition', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-gray-500 mt-1">미리보기에서 칸을 클릭해도 설정됩니다</p>
              </div>
            </div>

            {/* 표시 정보 */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 text-sm">라벨에 표시할 정보</h4>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayOptions.showName}
                    onChange={(e) => setDisplayOptions({...displayOptions, showName: e.target.checked})}
                    className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">제품명</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayOptions.showPrice}
                    onChange={(e) => setDisplayOptions({...displayOptions, showPrice: e.target.checked})}
                    className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">판매가격</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayOptions.showBarcode}
                    onChange={(e) => setDisplayOptions({...displayOptions, showBarcode: e.target.checked})}
                    className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">바코드 번호</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayOptions.showLocation}
                    onChange={(e) => setDisplayOptions({...displayOptions, showLocation: e.target.checked})}
                    className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">위치</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayOptions.showCategory}
                    onChange={(e) => setDisplayOptions({...displayOptions, showCategory: e.target.checked})}
                    className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">카테고리</span>
                </label>
              </div>
            </div>

            {/* 저장된 프리셋 */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-700 text-sm">저장된 설정</h4>
                <button
                  onClick={() => setShowPresetSave(!showPresetSave)}
                  className="px-2 py-1 text-xs bg-brand-600 text-white rounded hover:bg-brand-700 transition flex items-center"
                >
                  <Icons.Add className="w-3 h-3 mr-1" />
                  저장
                </button>
              </div>

              {showPresetSave && (
                <div className="p-2 bg-brand-50 border border-brand-200 rounded-lg">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                      placeholder="이름 입력..."
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      onKeyPress={(e) => e.key === 'Enter' && savePreset()}
                    />
                    <button
                      onClick={savePreset}
                      className="px-3 py-1 text-xs bg-brand-600 text-white rounded hover:bg-brand-700 transition"
                    >
                      저장
                    </button>
                  </div>
                </div>
              )}

              {savedPresets.length > 0 ? (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {savedPresets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded"
                    >
                      <button
                        onClick={() => loadPreset(preset)}
                        className="flex-1 text-left text-sm text-gray-700 hover:text-brand-600 transition truncate"
                      >
                        {preset.name}
                      </button>
                      <button
                        onClick={() => deletePreset(preset.id)}
                        className="p-1 text-red-500 hover:text-red-700 transition"
                      >
                        <Icons.Trash className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">저장된 설정이 없습니다</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2열 레이아웃: 제품 목록 + 미리보기 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:hidden">
        {/* 제품 선택 목록 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <h3 className="text-lg font-bold text-brand-900 mb-4">제품 목록</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredInventory.length === 0 ? (
              <p className="text-center text-gray-500 py-8">검색 결과가 없습니다.</p>
            ) : (
              filteredInventory.map((item) => (
                <div
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition ${
                    selectedItems.includes(item.id)
                      ? 'border-brand-600 bg-brand-50'
                      : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => {}}
                      className="w-5 h-5 text-brand-600 rounded focus:ring-brand-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{item.productName}</p>
                      <p className="text-xs text-gray-500">바코드: {item.barcodeId}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600">{item.location}</p>
                    <p className="text-sm font-medium text-brand-600">
                      {parseInt(item.salePrice || 0).toLocaleString()}원
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 미리보기 영역 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <h3 className="text-lg font-bold text-brand-900 mb-4">
            인쇄 미리보기 (A4 라벨지)
          </h3>

          {selectedItems.length === 0 ? (
            <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-center">
                <Icons.FileText className="w-16 h-16 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500">왼쪽에서 제품을 선택하세요</p>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-1">
                  📌 시작 위치를 선택하려면 아래 칸을 클릭하세요
                </p>
                <p className="text-xs text-blue-600">
                  이미 사용한 라벨이 있을 때, 시작할 칸을 클릭하면 그 위치부터 인쇄됩니다
                </p>
              </div>

              {/* 용지 그리드 미리보기 */}
              <div className="max-h-[500px] overflow-y-auto p-4 bg-white border-2 border-gray-300 rounded-lg">
                {/* 용지 비율 컨테이너 */}
                <div className="mx-auto bg-white shadow-lg" style={{
                  width: '100%',
                  maxWidth: '500px',
                  aspectRatio: `${currentPaperSettings.paperWidth}/${currentPaperSettings.paperHeight}`
                }}>
                  <div
                    className="grid p-4 h-full"
                    style={{
                      gridTemplateColumns: `repeat(${currentPaperSettings.columns}, 1fr)`,
                      gridAutoRows: 'minmax(0, 1fr)',
                      columnGap: `${currentPaperSettings.gapHorizontal * 2}px`,
                      rowGap: `${currentPaperSettings.gapVertical * 2}px`
                    }}
                  >
                    {Array.from({
                      length: currentPaperSettings.rows > 0
                        ? currentPaperSettings.columns * currentPaperSettings.rows
                        : Math.max(currentPaperSettings.columns * 8, printItems.length)
                    }).map((_, index) => {
                      const position = index + 1;
                      const isStartPosition = position === printerSettings.startPosition;
                      const hasBarcode = printItems[index] !== null && printItems[index] !== undefined;
                      const isEmpty = position < printerSettings.startPosition;

                      return (
                        <div
                          key={index}
                          onClick={() => handleSettingChange('startPosition', position)}
                          className={`
                            relative border-2 rounded cursor-pointer transition-all
                            flex flex-col items-center justify-center p-1 text-center
                            ${isStartPosition ? 'border-green-500 bg-green-50 ring-2 ring-green-300' : ''}
                            ${isEmpty && !isStartPosition ? 'border-dashed border-gray-300 bg-gray-50' : ''}
                            ${hasBarcode && !isEmpty && !isStartPosition ? 'border-brand-400 bg-brand-50' : ''}
                            ${!hasBarcode && !isEmpty && !isStartPosition ? 'border-gray-300 bg-white' : ''}
                            hover:shadow-md hover:scale-105
                          `}
                          title={`칸 ${position}${isStartPosition ? ' (시작 위치)' : ''}`}
                        >
                          {/* 위치 번호 */}
                          <div className={`text-xs font-bold mb-1 ${
                            isStartPosition ? 'text-green-700' :
                            hasBarcode ? 'text-brand-700' :
                            'text-gray-400'
                          }`}>
                            {position}
                          </div>

                          {/* 상태 표시 */}
                          {isStartPosition && (
                            <div className="text-green-600 text-xs font-bold">
                              ▶ 시작
                            </div>
                          )}
                          {hasBarcode && !isEmpty && printItems[index] && (
                            <div className="text-xs text-brand-600 truncate w-full px-1">
                              {codeType === 'qrcode' && qrCodes[printItems[index].id] && (
                                <div className="flex justify-center">
                                  <img
                                    src={qrCodes[printItems[index].id]}
                                    alt="QR"
                                    className="w-8 h-8"
                                  />
                                </div>
                              )}
                              {codeType === 'barcode' && printItems[index].productName?.substring(0, 8)}
                            </div>
                          )}
                          {isEmpty && !isStartPosition && (
                            <div className="text-xs text-gray-400">
                              빈 칸
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 범례 */}
              <div className="mt-4 flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-green-500 bg-green-50 rounded"></div>
                  <span>시작 위치</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-brand-400 bg-brand-50 rounded"></div>
                  <span>인쇄될 바코드</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-dashed border-gray-300 bg-gray-50 rounded"></div>
                  <span>사용 안 함</span>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-3 text-center font-medium">
                총 {selectedInventory.length}개 바코드 | 시작: {printerSettings.startPosition}번 칸 |
                {currentPaperSettings.columns}칸 × {currentPaperSettings.rows}줄 |
                {paperType === 'label' ? labelPaperPresets[selectedLabelPaper].name :
                 paperType === 'thermal' ? '감열지' : '사용자 정의'}
              </p>
            </>
          )}
        </div>
      </div>

      {/* 인쇄 영역 */}
      <div id="print-area" className="hidden print:block">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${currentPaperSettings.columns}, ${currentPaperSettings.labelWidth}mm)`,
            gridAutoRows: `${currentPaperSettings.labelHeight}mm`,
            columnGap: `${currentPaperSettings.gapHorizontal}mm`,
            rowGap: `${currentPaperSettings.gapVertical}mm`
          }}
        >
          {fullGridItems.map((item, index) => (
            <div
              key={index}
              className="barcode-item border border-gray-300 p-3 text-center bg-white overflow-hidden"
            >
              {item ? (
                <>
                  {/* 제품 정보 */}
                  <div className="mb-2 space-y-0.5">
                    {displayOptions.showName && (
                      <p className="text-sm font-medium truncate">{item.productName}</p>
                    )}
                    {displayOptions.showPrice && item.salePrice && (
                      <p className="text-xs text-gray-600">{parseInt(item.salePrice || 0).toLocaleString()}원</p>
                    )}
                    {displayOptions.showLocation && item.location && (
                      <p className="text-xs text-gray-600">{item.location}</p>
                    )}
                    {displayOptions.showCategory && item.category && (
                      <p className="text-xs text-gray-600">{item.category}</p>
                    )}
                  </div>

                  {/* 바코드/QR 코드 */}
                  {item.barcodeId && (
                    <>
                      <div className="flex justify-center items-center">
                        {codeType === 'barcode' ? (
                          <Barcode
                            value={item.barcodeId}
                            width={sizeConfig.width}
                            height={sizeConfig.height}
                            fontSize={sizeConfig.fontSize}
                            margin={3}
                          />
                        ) : (
                          qrCodes[item.id] && (
                            <img
                              src={qrCodes[item.id]}
                              alt={`QR: ${item.productName}`}
                              style={{
                                width: `${sizeConfig.height * 2}px`,
                                height: `${sizeConfig.height * 2}px`
                              }}
                            />
                          )
                        )}
                      </div>
                      {displayOptions.showBarcode && (
                        <p className="text-xs text-gray-600 mt-1">{item.barcodeId}</p>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="h-full opacity-0">빈 칸</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BarcodePrint;
